import {DateTime} from "luxon";
import {get} from "lit-translate";
import {Entity} from "./entity";
import {rootLabelKey} from "../helpers/issue-type-root-label.helper";

export class IssueType extends Entity<IssueType> {

    private _deletedAt: DateTime | null = null;

    public get deletedAt(): DateTime | null {
        return this._deletedAt;
    }

    public set deletedAt(value: DateTime | null) {
        this._deletedAt = value;
    }

    public get isDeleted(): boolean {
        return this._deletedAt instanceof DateTime;
    }

    private _parentUid: string | null = null;

    /** ShortId of the root type this type belongs to; null for a root (or before the mapping). */
    public get parentUid(): string | null {
        return this._parentUid;
    }

    public set parentUid(value: string | null) {
        this._parentUid = value;
        this._setOriginalValue(`parentUid`, value);
    }

    private _isStatusless: boolean = false;

    /**
     * Whether issues of this type carry no status (absence, public holiday, reminder families). Backend
     * state that follows the root, like canMoveRoot: not change-tracked, so a type save never sends it
     * (the type commands refuse it).
     */
    public get isStatusless(): boolean {
        return this._isStatusless;
    }

    public set isStatusless(value: boolean) {
        this._isStatusless = value;
    }

    private _isAbstract: boolean = false;

    /**
     * Whether no issue may name this type, only its children (the Absence root, once data migration 5b
     * ran). Backend state like isStatusless: not change-tracked, so a type save never sends it (the
     * type commands refuse it).
     */
    public get isAbstract(): boolean {
        return this._isAbstract;
    }

    public set isAbstract(value: boolean) {
        this._isAbstract = value;
    }

    private _canMoveRoot: boolean | null = null;

    /** Whether the backend lets this type move to another root (no issue names it); null if not known. */
    public get canMoveRoot(): boolean | null {
        return this._canMoveRoot;
    }

    public set canMoveRoot(value: boolean | null) {
        this._canMoveRoot = value;
    }

    private _canDelete: boolean | null = null;

    /** Whether the backend lets this type be deleted (D46 and the tree rules); null if not known. */
    public get canDelete(): boolean | null {
        return this._canDelete;
    }

    public set canDelete(value: boolean | null) {
        this._canDelete = value;
    }

    private _isFixed: boolean = false;

    get isFixed(): boolean {
        return this._isFixed;
    }

    set isFixed(value: boolean) {
        this._isFixed = value;
    }

    /**
     * @deprecated
     */
    private _include: string = ``;

    /**
     * @deprecated
     */
    public get include(): string {
        return this._include;
    }

    /**
     * @deprecated
     */
    public set include(value: string) {
        this._include = value;
        this._setOriginalValue(`include`, value);
    }

    private _shortcut: string = ``;

    public get shortcut(): string {
        return this._shortcut;
    }

    public set shortcut(value: string) {
        this._shortcut = value;
        this._setOriginalValue(`shortcut`, value);
    }

    /**
     * @deprecated
     */
    private _cssClasses: string = ``;

    /**
     * @deprecated
     */
    public get cssClasses(): string {
        return this._cssClasses;
    }

    /**
     * @deprecated
     */
    public set cssClasses(value: string) {
        this._cssClasses = value;
        this._setOriginalValue(`cssClasses`, value);
    }

    private _showInHeader: boolean = true;

    public get showInHeader(): boolean {
        return this._showInHeader;
    }

    public set showInHeader(value: boolean) {
        this._showInHeader = value;
        this._setOriginalValue(`showInHeader`, value);
    }

    private _description: string = ``;

    public get description(): string {
        return this._description;
    }

    public set description(value: string) {
        this._description = value;
        this._setOriginalValue(`description`, value);
    }

    /**
     * The name shown everywhere a type is listed or printed: a root by its translation key (D54: the same
     * on every tenant, "Aufgabe", "Abwesenheit", …), a child by its description. While a root's key has no
     * translation (lit-translate answers "[key]"), the description is shown. Calling lit-translate from the
     * entity is a deliberate trade-off: every combobox titles its items with toString().
     */
    public toString(): string {
        const key = rootLabelKey(this.uid);
        if (!key) {
            return this.description;
        }
        const label = get(key);
        return label && label !== `[${key}]` ? label : this.description;
    }
}